import { FilterOperator, useSearchProctorAuditLogQuery } from '@exam-center/graphql';
import { DataGrid, type GridSortModel, InfoOutlinedIcon, NoRowsOverlay } from '@shared/components';

import { useProctorActivityLogSearchParams } from '../../hooks';

import { columns } from './column';
import { ORDER_TYPE } from './utils';

export default function ProctorActivityLogList() {
  const [{ search, page, pageSize, sort, field }, setSearchParams] =
    useProctorActivityLogSearchParams();

  const handleSortModelChange = (sorting: GridSortModel) => {
    const field = sorting[0]?.field ?? 'actionTime';
    const sort = sorting[0]?.sort ?? 'desc';
    setSearchParams({ sort: sort, field: field });
  };

  const { data, loading } = useSearchProctorAuditLogQuery({
    variables: {
      searchProctorAuditLogInput: {
        order: {
          field,
          type: ORDER_TYPE[sort],
        },
        filter: {
          searchText: {
            fields: ['detail'],
            value: search,
          },
          conditions: [
            {
              field: 'executionResult',
              operator: FilterOperator.Equal,
              value: 'SUCCESS',
            },
          ],
        },
        paging: {
          currentPage: page,
          pageSize,
        },
      },
    },
    fetchPolicy: 'network-only',
  });

  const total = data?.ecSearchProctorAuditLog.total || 0;

  const isSearchResultEmpty = total === 0 && search.trim() !== '';
  return (
    <DataGrid
      rows={data?.ecSearchProctorAuditLog.proctorAuditLogs || []}
      columns={columns}
      getRowHeight={() => 'auto'}
      columnHeaderHeight={48}
      disableColumnMenu
      sortingMode='server'
      onSortModelChange={handleSortModelChange}
      loading={loading}
      slots={{
        noRowsOverlay: () =>
          !isSearchResultEmpty ? (
            <NoRowsOverlay icon={InfoOutlinedIcon} title='ยังไม่มีข้อมูล Activity Log' />
          ) : (
            <NoRowsOverlay />
          ),
      }}
      slotProps={{
        footer: {
          total,
          pagination: {
            page: page,
            count: total,
            rowsPerPage: pageSize,
            onPageChange: (_, newPage: number) => {
              setSearchParams({ search, page: newPage });
            },
            onRowsPerPageChange: (event) => {
              const newPageSize = parseInt(event.target.value, 10);
              setSearchParams({ search, page: 0, pageSize: newPageSize });
            },
          },
        },
      }}
      sx={(theme) => ({
        backgroundColor: theme.palette.background.paper,
        borderRadius: theme.spacing(2),
        border: `1px solid ${theme.palette.grey[300]}`,
        '& .MuiDataGrid-columnHeaders': {
          backgroundColor: theme.palette.grey[100],
          borderBottom: `1px solid ${theme.palette.grey[300]}`,
        },
        '& .MuiDataGrid-row .MuiDataGrid-cell': {
          padding: theme.spacing(3),
          borderBottom: `1px solid ${theme.palette.grey[300]}`,
          ...theme.typography.body2,
        },
        '& .MuiDataGrid-columnHeader': {
          px: 3,
        },
        '--DataGrid-overlayHeight': '400px',
      })}
    />
  );
}
